import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { 
  MessageSquare, Pin, Bell, Calendar, User, Users, Check, 
  ChevronLeft, Send, Plus, Search, Filter, Trash2, Edit2,
  AlertTriangle, Mail, Clock, Eye, CheckCircle
} from 'lucide-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import AnnouncementComposer from '../components/AnnouncementComposer';
import Modal from '../components/Modal';
import './StaffMessages.css';

function StaffMessages() {
  const { 
    state, 
    getAnnouncements,
    updateInternalMessage,
    deleteInternalMessage,
    getEmployeeById
  } = useApp();
  const { internalMessages, employees } = state;

  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showComposer, setShowComposer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  const activeEmployees = employees.filter(e => e.status === 'active');

  // Filter and sort messages
  const filteredMessages = useMemo(() => {
    let msgs = [...internalMessages];

    // Apply filter
    switch (filter) {
      case 'announcements':
        msgs = msgs.filter(m => m.type === 'announcement');
        break;
      case 'direct':
        msgs = msgs.filter(m => m.type === 'direct');
        break;
      case 'schedule':
        msgs = msgs.filter(m => m.type === 'schedule_update');
        break;
      case 'pinned':
        msgs = msgs.filter(m => m.isPinned);
        break;
      default:
        break;
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      msgs = msgs.filter(m => 
        m.subject?.toLowerCase().includes(query) ||
        m.body?.toLowerCase().includes(query) ||
        m.toName?.toLowerCase().includes(query)
      );
    }

    // Sort: pinned first, then by date
    return msgs.sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [internalMessages, filter, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const total = internalMessages.length;
    const announcements = internalMessages.filter(m => m.type === 'announcement').length;
    const direct = internalMessages.filter(m => m.type === 'direct').length;
    const pinned = internalMessages.filter(m => m.isPinned).length;
    const pendingAck = internalMessages.filter(m => 
      m.requiresAcknowledgment && 
      m.acknowledgedBy?.length < activeEmployees.length
    ).length;
    return { total, announcements, direct, pinned, pendingAck };
  }, [internalMessages, activeEmployees]);

  // Get acknowledgment status for a message
  const getAckStatus = (message) => {
    if (!message.requiresAcknowledgment) return null;
    const ackCount = message.acknowledgedBy?.length || 0;
    const totalNeeded = message.toId === 'all' ? activeEmployees.length : 1;
    return { ackCount, totalNeeded, complete: ackCount >= totalNeeded };
  };

  // Get read status for a message
  const getReadStatus = (message) => {
    const readCount = message.readBy?.length || 0;
    const totalNeeded = message.toId === 'all' ? activeEmployees.length : 1;
    return { readCount, totalNeeded };
  };

  // Handle delete
  const handleDelete = async () => {
    if (!messageToDelete) return;
    await deleteInternalMessage(messageToDelete.id);
    setMessageToDelete(null);
    setShowDeleteConfirm(false);
    if (selectedMessage?.id === messageToDelete.id) {
      setSelectedMessage(null);
    }
  };

  // Toggle pin
  const handleTogglePin = async (message) => {
    await updateInternalMessage(message.id, { isPinned: !message.isPinned });
  };

  // Get message type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'announcement': return <Bell size={16} className="type-icon announcement" />;
      case 'direct': return <User size={16} className="type-icon direct" />;
      case 'schedule_update': return <Calendar size={16} className="type-icon schedule" />;
      default: return <MessageSquare size={16} className="type-icon" />;
    }
  };

  // Get recipient display
  const getRecipientDisplay = (message) => {
    if (message.toId === 'all') return 'All Employees';
    if (message.toType === 'team') return `Team: ${message.toId}`;
    return message.toName || 'Unknown';
  };

  return (
    <div className="staff-messages-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1><MessageSquare size={28} /> Staff Messages</h1>
          <p className="subtitle">Internal communications and announcements</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowComposer(true)}>
            <Plus size={18} /> New Announcement
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card" onClick={() => setFilter('all')}>
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total Messages</span>
        </div>
        <div className="stat-card" onClick={() => setFilter('announcements')}>
          <Bell size={18} className="stat-icon" />
          <span className="stat-value">{stats.announcements}</span>
          <span className="stat-label">Announcements</span>
        </div>
        <div className="stat-card" onClick={() => setFilter('direct')}>
          <User size={18} className="stat-icon" />
          <span className="stat-value">{stats.direct}</span>
          <span className="stat-label">Direct Messages</span>
        </div>
        <div className="stat-card" onClick={() => setFilter('pinned')}>
          <Pin size={18} className="stat-icon" />
          <span className="stat-value">{stats.pinned}</span>
          <span className="stat-label">Pinned</span>
        </div>
        {stats.pendingAck > 0 && (
          <div className="stat-card warning">
            <AlertTriangle size={18} className="stat-icon" />
            <span className="stat-value">{stats.pendingAck}</span>
            <span className="stat-label">Pending Ack</span>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="messages-layout">
        {/* Messages List */}
        <div className="messages-list-panel">
          {/* Search and Filter */}
          <div className="list-toolbar">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="filter-buttons">
              <button 
                className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${filter === 'announcements' ? 'active' : ''}`}
                onClick={() => setFilter('announcements')}
              >
                <Bell size={14} /> Announcements
              </button>
              <button 
                className={`filter-btn ${filter === 'direct' ? 'active' : ''}`}
                onClick={() => setFilter('direct')}
              >
                <User size={14} /> Direct
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="messages-list">
            {filteredMessages.length === 0 ? (
              <div className="empty-state">
                <MessageSquare size={48} />
                <h3>No messages yet</h3>
                <p>Create an announcement to communicate with your team</p>
                <button className="btn btn-primary" onClick={() => setShowComposer(true)}>
                  <Plus size={16} /> New Announcement
                </button>
              </div>
            ) : (
              filteredMessages.map(message => {
                const ackStatus = getAckStatus(message);
                const readStatus = getReadStatus(message);
                const isSelected = selectedMessage?.id === message.id;

                return (
                  <div 
                    key={message.id}
                    className={`message-item ${isSelected ? 'selected' : ''} ${message.isPinned ? 'pinned' : ''}`}
                    onClick={() => setSelectedMessage(message)}
                  >
                    <div className="message-item-header">
                      <div className="message-type">
                        {getTypeIcon(message.type)}
                        {message.isPinned && <Pin size={12} className="pin-icon" />}
                      </div>
                      <span className="message-time">
                        {formatDistanceToNow(parseISO(message.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="message-subject">{message.subject}</div>
                    <div className="message-meta">
                      <span className="recipient">
                        To: {getRecipientDisplay(message)}
                      </span>
                      {ackStatus && (
                        <span className={`ack-status ${ackStatus.complete ? 'complete' : ''}`}>
                          <Check size={12} />
                          {ackStatus.ackCount}/{ackStatus.totalNeeded}
                        </span>
                      )}
                      <span className="read-status">
                        <Eye size={12} />
                        {readStatus.readCount}/{readStatus.totalNeeded}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div className="message-detail-panel">
          {selectedMessage ? (
            <>
              <div className="detail-header">
                <div className="detail-title">
                  {getTypeIcon(selectedMessage.type)}
                  <h2>{selectedMessage.subject}</h2>
                </div>
                <div className="detail-actions">
                  <button 
                    className={`btn-icon ${selectedMessage.isPinned ? 'active' : ''}`}
                    onClick={() => handleTogglePin(selectedMessage)}
                    title={selectedMessage.isPinned ? 'Unpin' : 'Pin'}
                  >
                    <Pin size={18} />
                  </button>
                  <button 
                    className="btn-icon danger"
                    onClick={() => {
                      setMessageToDelete(selectedMessage);
                      setShowDeleteConfirm(true);
                    }}
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              <div className="detail-meta">
                <div className="meta-item">
                  <Clock size={14} />
                  {format(parseISO(selectedMessage.createdAt), 'PPpp')}
                </div>
                <div className="meta-item">
                  <Users size={14} />
                  To: {getRecipientDisplay(selectedMessage)}
                </div>
                {selectedMessage.sendEmail && (
                  <div className="meta-item">
                    <Mail size={14} />
                    Email sent
                  </div>
                )}
              </div>

              <div className="detail-body">
                <div className="message-content">
                  {selectedMessage.body?.split('\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </div>

              {/* Acknowledgment Status */}
              {selectedMessage.requiresAcknowledgment && (
                <div className="ack-section">
                  <h4>
                    <Check size={16} />
                    Acknowledgments ({selectedMessage.acknowledgedBy?.length || 0} / {activeEmployees.length})
                  </h4>
                  <div className="ack-list">
                    {activeEmployees.map(emp => {
                      const hasAcked = selectedMessage.acknowledgedBy?.includes(emp.id);
                      return (
                        <div key={emp.id} className={`ack-item ${hasAcked ? 'acknowledged' : ''}`}>
                          <div className="ack-avatar">{emp.avatar || emp.firstName[0]}</div>
                          <span className="ack-name">{emp.firstName} {emp.lastName}</span>
                          {hasAcked ? (
                            <CheckCircle size={16} className="ack-check" />
                          ) : (
                            <Clock size={16} className="ack-pending" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Read Status */}
              <div className="read-section">
                <h4>
                  <Eye size={16} />
                  Read by ({selectedMessage.readBy?.length || 0} / {selectedMessage.toId === 'all' ? activeEmployees.length : 1})
                </h4>
                <div className="read-list">
                  {selectedMessage.toId === 'all' ? (
                    activeEmployees.map(emp => {
                      const hasRead = selectedMessage.readBy?.includes(emp.id);
                      return (
                        <div key={emp.id} className={`read-item ${hasRead ? 'read' : ''}`}>
                          <div className="read-avatar">{emp.avatar || emp.firstName[0]}</div>
                          <span className="read-name">{emp.firstName}</span>
                          {hasRead && <Check size={14} />}
                        </div>
                      );
                    })
                  ) : (
                    <div className={`read-item ${selectedMessage.readBy?.length > 0 ? 'read' : ''}`}>
                      <span className="read-name">{selectedMessage.toName}</span>
                      {selectedMessage.readBy?.length > 0 && <Check size={14} />}
                    </div>
                  )}
                </div>
              </div>

              {/* Replies */}
              {selectedMessage.replies?.length > 0 && (
                <div className="replies-section">
                  <h4>Replies ({selectedMessage.replies.length})</h4>
                  <div className="replies-list">
                    {selectedMessage.replies.map((reply, i) => (
                      <div key={i} className="reply-item">
                        <div className="reply-header">
                          <strong>{reply.fromName}</strong>
                          <span>{formatDistanceToNow(parseISO(reply.createdAt), { addSuffix: true })}</span>
                        </div>
                        <div className="reply-body">{reply.body}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="no-selection">
              <MessageSquare size={48} />
              <h3>Select a message</h3>
              <p>Choose a message from the list to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Announcement Composer Modal */}
      <AnnouncementComposer
        isOpen={showComposer}
        onClose={() => setShowComposer(false)}
        onSuccess={() => {
          // Could show a success toast here
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setMessageToDelete(null);
        }}
        title="Delete Message"
      >
        <div className="delete-confirm">
          <AlertTriangle size={48} className="warning-icon" />
          <p>Are you sure you want to delete this message?</p>
          <p className="message-preview">"{messageToDelete?.subject}"</p>
          <p className="warning-text">This action cannot be undone.</p>
          <div className="confirm-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setMessageToDelete(null);
              }}
            >
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              <Trash2 size={16} /> Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default StaffMessages;
